import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField, Slide, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Pagination, Skeleton } from '@mui/material';
import { getData, postData } from '../../Webservice/Webservice';

const Rewards = () => {
  const [showForm, setShowForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showDetails, setShowDetails] = useState(false); // New state for details panel
  const [formData, setFormData] = useState({
    spendAmountPerPoint: '',
    pointValue: '',
    expiryMonths: ''
  });
  const [rewardConfig, setRewardConfig] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [detailsData, setDetailsData] = useState([]); // New state for details data
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 }); // Pagination state for history
  const [detailsMeta, setDetailsMeta] = useState({ page: 1, totalPages: 1, total: 0 }); // Pagination state for details
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(true); // Loading state for details
  const [error, setError] = useState(null);
  const [submitStatus, setSubmitStatus] = useState(null);
  const PAGE_SIZE = 10;

  useEffect(() => {
    const fetchRewardConfig = async () => {
      try {
        const tokenValue = sessionStorage.getItem('token');
        const token = tokenValue ? `Bearer ${tokenValue}` : '';
        const response = await getData('reward/config/view', null, token);
        if (response.success) {
          setRewardConfig(response.config);
          setFormData({
            spendAmountPerPoint: response.config.spendAmountPerPoint,
            pointValue: response.config.pointValue,
            expiryMonths: response.config.expiryMonths
          });
        } else {
          // Don't set error if config not found - user can create one
          console.log('No reward configuration found, user can create one');
        }
      } catch (err) {
        // Don't set error for network issues - allow user to proceed
        console.log('Error fetching reward config, user can still create one:', err);
      }
    };

    const fetchHistoryData = async (page = 1) => {
      try {
        const tokenValue = sessionStorage.getItem('token');
        const token = tokenValue ? `Bearer ${tokenValue}` : '';
        const params = {
          page,
          limit: PAGE_SIZE,
        };
        const response = await getData('reward/config/log', params, token);
        if (response.logHistory) {
          setHistoryData(response.logHistory);
          const metaData = response.meta || { page: 1, totalPages: 1, total: 0 };
          setMeta({
            page: metaData.page,
            totalPages: metaData.totalPages,
            total: metaData.total
          });
        } else {
          console.log('No history data found');
        }
      } catch (err) {
        console.log('Error fetching history data:', err);
      }
    };

    const fetchDetailsData = async (page = 1) => {
      try {
        setDetailsLoading(true);
        const tokenValue = sessionStorage.getItem('token');
        const token = tokenValue ? `Bearer ${tokenValue}` : '';
        const params = {
          page,
          limit: PAGE_SIZE,
        };
        const response = await getData('reward/config/details', params, token);
        if (Array.isArray(response)) {
          setDetailsData(response);
          const total = response.length; // Adjust based on actual response structure
          setDetailsMeta({
            page,
            totalPages: Math.ceil(total / PAGE_SIZE),
            total
          });
        } else {
          console.log('No details data found');
        }
      } catch (err) {
        console.log('Error fetching reward details data:', err);
      } finally {
        setDetailsLoading(false);
      }
    };

    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchRewardConfig(), fetchHistoryData(meta.page), fetchDetailsData(detailsMeta.page)]);
      } catch (err) {
        console.error('Error in fetchData:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [meta.page, detailsMeta.page]);

  const handleButtonClick = () => {
    setShowForm(!showForm);
    setSubmitStatus(null);
  };

  const handleHistoryClick = () => {
    setShowHistory(!showHistory);
    setShowDetails(false); // Close details panel if open
  };

  const handleDetailsClick = () => {
    setShowDetails(!showDetails);
    setShowHistory(false); // Close history panel if open
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
    try {
      const tokenValue = sessionStorage.getItem('token');
      const token = tokenValue ? `Bearer ${tokenValue}` : '';
      const response = await postData('reward/config/add', formData, token);
      if (response) {
        setRewardConfig({
          ...rewardConfig,
          spendAmountPerPoint: formData.spendAmountPerPoint,
          pointValue: formData.pointValue,
          expiryMonths: formData.expiryMonths
        });
        setSubmitStatus('Reward configuration updated successfully!');
        setShowForm(false);
      } else {
        setSubmitStatus('Failed to update reward configuration');
      }
    } catch (err) {
      setSubmitStatus('Error updating reward configuration');
      console.error(err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handlePageChange = (event, newPage) => {
    setMeta(prev => ({ ...prev, page: newPage }));
  };

  const handleDetailsPageChange = (event, newPage) => {
    setDetailsMeta(prev => ({ ...prev, page: newPage }));
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        maxWidth: 1200,
        margin: '0 auto',
        minHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        position: 'relative',
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Typography variant="h3" component="h1" sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          fontFamily: 'Mulish, sans-serif'
        }}>
          Rewards Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleHistoryClick}
            sx={{
              backgroundColor: 'rgb(255, 209, 2)',
              border: '1px solid black',
              color: 'black',
              padding: '8px 16px',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontFamily: 'Mulish, sans-serif',
              fontSize: '0.9rem',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgb(230, 189, 0)',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
            }}
          >
            {showHistory ? 'Close History' : 'View History'}
          </Button>
          <Button
            variant="contained"
            onClick={handleDetailsClick}
            sx={{
              backgroundColor: 'rgb(255, 209, 2)',
              border: '1px solid black',
              color: 'black',
              padding: '8px 16px',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontFamily: 'Mulish, sans-serif',
              fontSize: '0.9rem',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgb(230, 189, 0)',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
            }}
          >
            {showDetails ? 'Close Details' : 'Rewards Details'}
          </Button>
        </Box>
      </Box>

      {/* Main Card */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexGrow: 1,
        }}
      >
        <Box
          sx={{
            maxWidth: '900px',
            width: '100%',
            backgroundColor: '#ffffff',
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            borderRadius: '12px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            transition: 'transform 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
            },
          }}
        >
          {/* Left Side: Image */}
          <Box
            sx={{
              flex: { xs: '0 0 auto', md: '0 0 320px' },
              width: { xs: '100%', md: '320px' },
              p: 2,
              bgcolor: '#f8f8f8',
            }}
          >
            <img
              src="./img/reward.png"
              alt="Reward"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '8px',
              }}
            />
          </Box>

          {/* Right Side: Content */}
          <Box
            sx={{
              flex: 1,
              p: { xs: 2, md: 3 },
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: '#1f1f1f',
                fontWeight: 700,
                fontFamily: 'Mulish, sans-serif',
              }}
            >
              Rewards
            </Typography>
            <Typography
              sx={{
                color: '#9fa7b1',
                fontFamily: 'Mulish, sans-serif',
                fontSize: '1.1rem',
                lineHeight: 1.8,
              }}
            >
              Your loyalty points summary
            </Typography>
            <Box sx={{ py: 2 }}>
              {loading ? (
                <Typography>Loading...</Typography>
              ) : !showForm ? (
                <>
                  <Typography
                    sx={{
                      color: '#4b5563',
                      fontFamily: 'Mulish, sans-serif',
                      fontSize: '0.95rem',
                      lineHeight: 1.8,
                      mb: 3,
                    }}
                  >
                    You earn 1 point for every ₹{rewardConfig?.spendAmountPerPoint || 100} spent, 
                    and points expire after {rewardConfig?.expiryMonths || 12} months.<br/>
                    1 point = ₹{rewardConfig?.pointValue || 1}
                  </Typography>
                  {submitStatus && (
                    <Typography
                      sx={{
                        color: submitStatus.includes('Error') || submitStatus.includes('Failed') ? 'red' : 'green',
                        fontFamily: 'Mulish, sans-serif',
                        mb: 2,
                      }}
                    >
                      {submitStatus}
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: 'rgb(255, 209, 2)',
                      border: '1px solid black',
                      color: 'black',
                      padding: '10px 24px',
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontFamily: 'Mulish, sans-serif',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'rgb(230, 189, 0)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      },
                    }}
                    onClick={handleButtonClick}
                  >
                    Update Rewards
                  </Button>
                </>
              ) : (
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    maxWidth: '400px',
                  }}
                >
                  <TextField
                    label="Spend Amount Per Point (₹)"
                    name="spendAmountPerPoint"
                    type="number"
                    value={formData.spendAmountPerPoint}
                    onChange={handleInputChange}
                    required
                    sx={{ fontFamily: 'Mulish, sans-serif' }}
                  />
                  <TextField
                    label="Point Value"
                    name="pointValue"
                    type="number"
                    value={formData.pointValue}
                    onChange={handleInputChange}
                    required
                    sx={{ fontFamily: 'Mulish, sans-serif' }}
                  />
                  <TextField
                    label="Expiry Months"
                    name="expiryMonths"
                    type="number"
                    value={formData.expiryMonths}
                    onChange={handleInputChange}
                    required
                    sx={{ fontFamily: 'Mulish, sans-serif' }}
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleButtonClick}
                      sx={{
                        border: '1px solid red',
                        color: 'red',
                        '&:hover': { borderColor: 'red', backgroundColor: 'rgba(255,0,0,0.1)' },
                        padding: '10px 24px',
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontFamily: 'Mulish, sans-serif',
                        fontSize: '1rem',
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      sx={{
                        backgroundColor: 'rgb(255, 209, 2)',
                        border: '1px solid black',
                        color: 'black',
                        padding: '10px 24px',
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontFamily: 'Mulish, sans-serif',
                        fontSize: '1rem',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'rgb(230, 189, 0)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        },
                      }}
                    >
                      Submit
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* History Panel */}
      <Slide direction="left" in={showHistory} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: { xs: '100%', sm: '955px' },
            height: '100%',
            bgcolor: '#ffffff',
            boxShadow: '0 0 24px rgba(0,0,0,0.2)',
            zIndex: 1300,
            p: 3,
            overflowY: 'auto',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'Mulish, sans-serif',
                fontWeight: 400,
              }}
            >
              Reward Configuration History
            </Typography>
            <Button
              variant="outlined"
              onClick={handleHistoryClick}
              sx={{
                color: 'red',
                fontFamily: 'Mulish, sans-serif',
                fontWeight: 600,
                borderColor: 'red',
                '&:hover': {
                  borderColor: 'red',
                  backgroundColor: 'rgba(255,0,0,0.1)',
                },
              }}
            >
              Close
            </Button>
          </Box>
          <TableContainer sx={{ mt: 5 }} component={Paper}>
            <Table sx={{ minWidth: 300 }} aria-label="history table">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 600 }}>Spend/Point (₹)</TableCell>
                  <TableCell sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 600 }}>Point Value</TableCell>
                  <TableCell sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 600 }}>Expiry (Months)</TableCell>
                  <TableCell sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 600 }}>Updated By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <TableRow key={idx}>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                    </TableRow>
                  ))
                ) : historyData.length > 0 ? (
                  historyData.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ fontFamily: 'Mulish, sans-serif' }}>{formatDate(row.date)}</TableCell>
                      <TableCell sx={{ fontFamily: 'Mulish, sans-serif' }}>{row.spendAmountPerPoint}</TableCell>
                      <TableCell sx={{ fontFamily: 'Mulish, sans-serif' }}>{row.pointValue}</TableCell>
                      <TableCell sx={{ fontFamily: 'Mulish, sans-serif' }}>{row.expiryMonths}</TableCell>
                      <TableCell sx={{ fontFamily: 'Mulish, sans-serif' }}>{row.user || 'Unknown'}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ fontFamily: 'Mulish, sans-serif', textAlign: 'center' }}>
                      No history data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, '& .MuiPagination-ul': { flexWrap: 'nowrap' } }}>
            <Pagination
              count={meta.totalPages}
              page={meta.page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </Box>
          {meta.total > 0 && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              Showing {(meta.page - 1) * PAGE_SIZE + 1} to {Math.min(meta.page * PAGE_SIZE, meta.total)} of {meta.total} entries
            </Typography>
          )}
        </Box>
      </Slide>

      {/* Details Panel */}
      <Slide direction="left" in={showDetails} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: { xs: '100%', sm: '1000px' },
            height: '100%',
            bgcolor: '#ffffff',
            boxShadow: '0 0 24px rgba(0,0,0,0.2)',
            zIndex: 1300,
            p: 3,
            overflowY: 'auto',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'Mulish, sans-serif',
                fontWeight: 400,
              }}
            >
              Reward Details
            </Typography>
            <Button
              variant="outlined"
              onClick={handleDetailsClick}
              sx={{
                color: 'red',
                fontFamily: 'Mulish, sans-serif',
                fontWeight: 600,
                borderColor: 'red',
                '&:hover': {
                  borderColor: 'red',
                  backgroundColor: 'rgba(255,0,0,0.1)',
                },
              }}
            >
              Close
            </Button>
          </Box>
          <TableContainer sx={{ mt: 5 }} component={Paper}>
            <Table sx={{ minWidth: 300 }} aria-label="details table">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  {/* <TableCell sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 600 }}>Reward ID</TableCell> */}
                  <TableCell sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 600 }}>Reward Name</TableCell>
                  <TableCell sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 600 }}>Total Available</TableCell>
                  <TableCell sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 600 }}>Total Redeemed</TableCell>
                  <TableCell sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 600 }}>Total Expired</TableCell>
                  <TableCell sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 600 }}>Total Points</TableCell>
                  <TableCell sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 600 }}>Amount/Point (₹)</TableCell>
                  <TableCell sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 600 }}>Value/Point</TableCell>
                  <TableCell sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 600 }}>Valid From</TableCell>
                  <TableCell sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 600 }}>Valid To</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detailsLoading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <TableRow key={idx}>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                    </TableRow>
                  ))
                ) : detailsData.length > 0 ? (
                  detailsData.map((row, index) => (
                    <TableRow key={index}>
                      {/* <TableCell sx={{ fontFamily: 'Mulish, sans-serif' }}>{row.rewardId}</TableCell> */}
                      <TableCell sx={{ fontFamily: 'Mulish, sans-serif' }}>{row.rewardName}</TableCell>
                      <TableCell sx={{ fontFamily: 'Mulish, sans-serif' }}>{row.totalAvailable}</TableCell>
                      <TableCell sx={{ fontFamily: 'Mulish, sans-serif' }}>{row.totalRedeemed}</TableCell>
                      <TableCell sx={{ fontFamily: 'Mulish, sans-serif' }}>{row.totalExpired}</TableCell>
                      <TableCell sx={{ fontFamily: 'Mulish, sans-serif' }}>{row.totalPoints}</TableCell>
                      <TableCell sx={{ fontFamily: 'Mulish, sans-serif' }}>{row.amountForPoint}</TableCell>
                      <TableCell sx={{ fontFamily: 'Mulish, sans-serif' }}>{row.valueForPoint}</TableCell>
                      <TableCell sx={{ fontFamily: 'Mulish, sans-serif' }}>{formatDate(row.validFrom)}</TableCell>
                      <TableCell sx={{ fontFamily: 'Mulish, sans-serif' }}>{formatDate(row.validTo)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ fontFamily: 'Mulish, sans-serif', textAlign: 'center' }}>
                      No reward details available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, '& .MuiPagination-ul': { flexWrap: 'nowrap' } }}>
            <Pagination
              count={detailsMeta.totalPages}
              page={detailsMeta.page}
              onChange={handleDetailsPageChange}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </Box>
          {detailsMeta.total > 0 && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              Showing {(detailsMeta.page - 1) * PAGE_SIZE + 1} to {Math.min(detailsMeta.page * PAGE_SIZE, detailsMeta.total)} of {detailsMeta.total} entries
            </Typography>
          )}
        </Box>
      </Slide>
    </Box>
  );
};

export default Rewards;